import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useProducts, money, cleanTitle } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useCart, toLine } from "@/lib/cart";

const norm = (s = "") => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const Result = ({ p, onClose, i }) => {
  const { t } = useI18n();
  const { checkout } = useCart();
  const single = p.variants.length === 1;
  return (
    <motion.div layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: i * 0.03 }}
      data-testid={`search-result-${p.handle}`} className="flex items-center gap-4 py-3 border-b border-white/5 group">
      <Link to={`/products/${p.handle}`} onClick={onClose} className="h-16 w-14 bg-white shrink-0"><img src={p.images[0]} alt="" className="h-full w-full object-contain p-1" /></Link>
      <Link to={`/products/${p.handle}`} onClick={onClose} className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{p.brand}</p>
        <p className="text-sm truncate group-hover:text-gold transition-colors">{cleanTitle(p.title)}</p>
        <p className="text-xs text-white/50">{money(p.price)}</p>
      </Link>
      {single && <button data-testid={`search-buy-${p.handle}`} onClick={() => checkout([toLine(p, p.variants[0])])} className="btn-gold h-9 px-3 sm:px-4 text-[9px] sm:text-[10px] shrink-0 max-w-[96px] sm:max-w-none leading-tight">{t("buy_now")}</button>}
    </motion.div>
  );
};

export const SearchDialog = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data = [] } = useProducts({});
  const results = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return data.slice(0, 5);
    return data.filter((p) => n.split(/\s+/).every((w) => norm(`${p.title} ${p.title_en || ""} ${p.brand} ${p.excerpt} ${p.tags.join(" ")}`).includes(w)));
  }, [q, data]);
  const close = () => { setOpen(false); setQ(""); };

  return (
    <>
      <button data-testid="header-search-button" onClick={() => setOpen(true)} className="text-[#e3cb94] hover:text-white transition-colors" aria-label={t("search")}><Search size={21} /></button>
      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent data-testid="search-dialog" className="bg-[#0e0d0b]/95 backdrop-blur-2xl border border-[#c9a55c]/25 max-w-2xl p-0 top-[12%] translate-y-0 [&>button]:hidden">
          <DialogTitle className="sr-only">{t("search")}</DialogTitle>
          <DialogDescription className="sr-only">{t("search_placeholder")}</DialogDescription>
          <div className="flex items-center gap-3 px-6 border-b border-white/10">
            <Search size={18} className="text-gold" />
            <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")}
              className="flex-1 h-16 bg-transparent text-lg font-display focus:outline-none placeholder:text-white/30" />
            <button data-testid="search-close-button" onClick={close} className="text-white/50 hover:text-white"><X size={18} /></button>
          </div>
          <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto" data-lenis-prevent>
            <p data-testid="search-results-label" className="eyebrow pt-5 pb-2">{q.trim() ? t("search_results", { n: results.length }) : t("search_popular")}</p>
            <AnimatePresence mode="popLayout">
              {results.map((p, i) => <Result key={p.id} p={p} i={i} onClose={close} />)}
            </AnimatePresence>
            {q.trim() && !results.length && <p data-testid="search-empty" className="text-sm text-white/50 py-8 text-center">{t("search_empty", { q })}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
